import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Grid, TextField, FormControl, InputLabel, Select, MenuItem, Button, Alert, Link } from '@mui/material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import SettingsErrorAlert from './SettingsErrorAlert';

const SourceSettings = () => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [readReport, setReadReport] = useState(null);
  const [form, setForm] = useState({
    active_source_type: 'base_local',
    base_local: {
      location: '',
      version: '',
      component_versions: {
        base_frontend: '',
        base_backend: '',
        base_database: ''
      }
    },
    ghe: { location: '', version: '' }
  });

  const load = async () => {
    try {
      const response = await api.get('/settings/source-config');
      const value = response.data.data;
      setForm({
        active_source_type: value.active_source_type || 'base_local',
        base_local: {
          location: value?.base_local?.location || '',
          version: value?.base_local?.version || '',
          component_versions: {
            base_frontend: value?.base_local?.component_versions?.base_frontend || '',
            base_backend: value?.base_local?.component_versions?.base_backend || '',
            base_database: value?.base_local?.component_versions?.base_database || ''
          }
        },
        ghe: value.ghe || { location: '', version: '' }
      });
      if (value?.read_report) {
        setReadReport(value.read_report);
      }
    } catch (err) {
      setError({
        message: err.response?.data?.message || err.message || 'Failed to load source settings',
        path: err.response?.data?.path,
        status: err.response?.status
      });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await api.put('/settings/source-config', form);
      setReadReport(response.data?.data?.read_report || null);
      toast.success('File location settings saved');
      load();
    } catch (err) {
      setError({
        message: err.response?.data?.message || err.message || 'Failed to save source settings',
        path: err.response?.data?.path,
        status: err.response?.status
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>File Location</Typography>
      {error && (
        <Box sx={{ mb: 2 }}>
          <SettingsErrorAlert message={error.message} path={error.path} status={error.status} />
          <Button variant="outlined" onClick={() => { setError(null); load(); }}>Retry</Button>
        </Box>
      )}
      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Default Source Type</InputLabel>
              <Select
                label="Default Source Type"
                value={form.active_source_type}
                onChange={(e) => setForm((p) => ({ ...p, active_source_type: e.target.value }))}
              >
                <MenuItem value="base_local">File Location (Local Base)</MenuItem>
                <MenuItem value="ghe_clone">GHE Location</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}><Typography variant="h6">Source Location Configuration</Typography></Grid>
          <Grid item xs={12}>
            <Alert severity="info">
              Admin enters a source location and saves. System auto-loads base frontend/backend/database versions as read-only values.
            </Alert>
          </Grid>
          {form.active_source_type === 'base_local' && (
            <>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Base File Location"
                  value={form.base_local.location}
                  onChange={(e) => setForm((p) => ({ ...p, base_local: { ...p.base_local, location: e.target.value } }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Detected Base Version" value={form.base_local.version || '-'} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Base Frontend Version"
                  value={form.base_local.component_versions.base_frontend || '-'}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Base Backend Version"
                  value={form.base_local.component_versions.base_backend || '-'}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Base Database Version"
                  value={form.base_local.component_versions.base_database || '-'}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </>
          )}

          {form.active_source_type === 'ghe_clone' && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6">GHE Source</Typography>
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="GHE Repository Location"
                  value={form.ghe.location}
                  onChange={(e) => setForm((p) => ({ ...p, ghe: { ...p.ghe, location: e.target.value } }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="GHE Source Version"
                  placeholder="1.2026.24.01"
                  value={form.ghe.version}
                  onChange={(e) => setForm((p) => ({ ...p, ghe: { ...p.ghe, version: e.target.value } }))}
                />
              </Grid>
            </>
          )}

          {readReport && (
            <>
              <Grid item xs={12}><Typography variant="h6">Read Report</Typography></Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  BASE_SOURCE_ROOT: {readReport.base_source_root || '-'} | Base root: {readReport.base_root || '-'} | Port strategy loaded: {readReport?.port_strategy?.is_loaded ? 'Yes' : 'No'}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Scan candidate roots (container tries these paths)"
                  value={(readReport?.candidate_roots || []).join('\n') || '-'}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Candidate root checks (exists | is_directory)"
                  value={(readReport?.candidate_root_checks || []).map((entry) => `${entry.path} | exists=${entry.exists} | is_directory=${entry.is_directory}`).join('\n') || '-'}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Frontend VERSION file read from"
                  value={readReport?.component_version_sources?.base_frontend || '-'}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Backend VERSION file read from"
                  value={readReport?.component_version_sources?.base_backend || '-'}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Database VERSION file read from"
                  value={readReport?.component_version_sources?.base_database || '-'}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Files successfully read"
                  value={(readReport?.files_read || []).join('\n') || '-'}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="PORT_STRATEGY attempted paths (docs/PORT_STRATEGY.md)"
                  value={(readReport?.port_strategy?.attempted_paths || []).join('\n') || '-'}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity={readReport?.source_scan?.selected_root ? 'success' : 'warning'}>
                  Source scan root: {readReport?.source_scan?.selected_root || 'No readable folder found from backend runtime'}
                  {readReport?.source_scan?.selected_root_open_link ? (
                    <>
                      {' '}|{' '}
                      <Link href={readReport.source_scan.selected_root_open_link} target="_blank" rel="noreferrer">
                        Open scanned source root
                      </Link>
                    </>
                  ) : null}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  label="Folders discovered in source scan"
                  value={(readReport?.source_scan?.folders || []).join('\n') || '-'}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={8}
                  label="Files discovered in source scan"
                  value={(readReport?.source_scan?.files || []).join('\n') || '-'}
                  InputProps={{ readOnly: true }}
                  helperText={readReport?.source_scan?.is_truncated ? 'Scan output truncated. Narrow location or increase scan limit in backend if needed.' : ''}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="outlined" onClick={load}>Reset</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default SourceSettings;

